import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronRight,
  CircleDot,
  Clipboard,
  Play,
  RefreshCw,
  Share2,
  Shield,
  Trophy,
  X
} from "./icons";
import { getQuestionById, loadQuestionBank, pickQuizQuestions, staticQuestionBank } from "./questions";
import type { QuizOptionImage, QuizQuestion } from "./types";

type Phase = "ready" | "playing" | "finished";

type AnswerRecord = {
  questionId: string;
  selectedIndex: number;
  isCorrect: boolean;
};

type QuizSession = {
  phase: Phase;
  currentIndex: number;
  answers: AnswerRecord[];
  questionIds: string[];
};

type QuestionLoadState = "idle" | "loading" | "error";

type ResultBand = {
  minPercent: number;
  title: string;
  label: string;
  summary: string;
  badgeLine: string;
  thresholdLabel: string;
};

const QUIZ_STORAGE_KEY = "realfantest.session.v1";
const QUESTIONS_PER_SESSION = 10;
const initialSession: QuizSession = {
  phase: "ready",
  currentIndex: 0,
  answers: [],
  questionIds: []
};

const resultBands: ResultBand[] = [
  {
    minPercent: 0.8,
    title: "Real Fan",
    label: "Certified stand regular",
    summary: "You read the match before the replay finds it.",
    badgeLine: "Your fan level: expert",
    thresholdLabel: "Real Fan 8+"
  },
  {
    minPercent: 0.6,
    title: "Matchday Regular",
    label: "Strong football radar",
    summary: "You know the big moments and enough detail to argue at halftime.",
    badgeLine: "Your fan level: regular",
    thresholdLabel: "Regular 6-7"
  },
  {
    minPercent: 0.35,
    title: "Casual Supporter",
    label: "Needs extra minutes",
    summary: "You have the headlines. The rulebook and archive need a little more time.",
    badgeLine: "Your fan level: casual",
    thresholdLabel: "Casual 4-5"
  },
  {
    minPercent: 0,
    title: "Fake Fan Alert",
    label: "Training ground required",
    summary: "You can still recover. Restart and earn the badge.",
    badgeLine: "Your fan level: beginner",
    thresholdLabel: "Fake 0-3"
  }
];

function getSessionQuestions(questionIds: string[], questions: QuizQuestion[]): QuizQuestion[] {
  const selectedQuestions = questionIds
    .map((questionId) => getQuestionById(questionId, questions))
    .filter((question): question is QuizQuestion => Boolean(question));
  return selectedQuestions.length > 0 ? selectedQuestions : questions.slice(0, QUESTIONS_PER_SESSION);
}

function sanitizeSession(
  value: Partial<QuizSession>,
  questions: QuizQuestion[],
  keepUnknownQuestionIds = false
): QuizSession {
  const phase = value.phase === "playing" || value.phase === "finished" ? value.phase : "ready";
  const questionIds = Array.isArray(value.questionIds)
    ? value.questionIds
        .filter((questionId) => keepUnknownQuestionIds || Boolean(getQuestionById(questionId, questions)))
        .slice(0, QUESTIONS_PER_SESSION)
    : [];
  const selectedQuestionCount = getSessionQuestions(questionIds, questions).length;
  const totalQuestions = Math.max(selectedQuestionCount, keepUnknownQuestionIds ? questionIds.length : 0, 1);
  const currentIndex =
    typeof value.currentIndex === "number"
      ? Math.min(Math.max(0, value.currentIndex), totalQuestions - 1)
      : 0;
  const answers = Array.isArray(value.answers)
    ? value.answers.filter((answer): answer is AnswerRecord => {
        const question = getQuestionById(answer.questionId, questions);
        return Boolean(
          typeof answer.selectedIndex === "number" &&
            answer.selectedIndex >= 0 &&
            typeof answer.isCorrect === "boolean" &&
            (question ? answer.selectedIndex < question.options.length : keepUnknownQuestionIds)
        );
      })
    : [];

  return { phase, currentIndex, answers, questionIds };
}

function loadSession(): QuizSession {
  if (typeof window === "undefined") {
    return initialSession;
  }

  try {
    const rawValue = window.localStorage.getItem(QUIZ_STORAGE_KEY);
    return rawValue ? sanitizeSession(JSON.parse(rawValue) as Partial<QuizSession>, staticQuestionBank, true) : initialSession;
  } catch {
    return initialSession;
  }
}

function loadInitialAppState(): { session: QuizSession; savedSession: QuizSession | null } {
  const loadedSession = loadSession();
  const hasSavedProgress =
    loadedSession.phase !== "ready" && loadedSession.questionIds.length > 0 && loadedSession.answers.length > 0;
  return {
    session: hasSavedProgress ? initialSession : loadedSession,
    savedSession: hasSavedProgress ? loadedSession : null
  };
}

function getResultBand(score: number, totalQuestions: number): ResultBand {
  const percent = score / totalQuestions;
  return resultBands.find((band) => percent >= band.minPercent) ?? resultBands[resultBands.length - 1];
}

function getShareText(score: number, totalQuestions: number, result: ResultBand): string {
  return `I scored ${score}/${totalQuestions} on Real Fan Test and ranked ${result.title}. Can you beat ${score}/${totalQuestions}? https://realfantest.com`;
}

export default function App() {
  const [initialAppState] = useState(loadInitialAppState);
  const [session, setSession] = useState<QuizSession>(initialAppState.session);
  const [savedSession, setSavedSession] = useState<QuizSession | null>(initialAppState.savedSession);
  const [questions, setQuestions] = useState<QuizQuestion[]>(staticQuestionBank);
  const [questionLoadState, setQuestionLoadState] = useState<QuestionLoadState>("idle");
  const [questionLoadError, setQuestionLoadError] = useState("");
  const [toast, setToast] = useState("");
  const mainPanelRef = useRef<HTMLDivElement>(null);
  const activeQuestions = useMemo(() => getSessionQuestions(session.questionIds, questions), [questions, session.questionIds]);
  const totalQuestions = activeQuestions.length > 0 ? activeQuestions.length : Math.max(session.questionIds.length, QUESTIONS_PER_SESSION);
  const currentQuestion = activeQuestions[session.currentIndex] ?? activeQuestions[0];
  const currentAnswer = currentQuestion
    ? session.answers.find((answer) => answer.questionId === currentQuestion.id)
    : undefined;
  const score = useMemo(() => session.answers.filter((answer) => answer.isCorrect).length, [session.answers]);
  const result = useMemo(() => getResultBand(score, totalQuestions), [score, totalQuestions]);
  const progress = Math.round((session.answers.length / totalQuestions) * 100);
  const hasMissingSessionQuestions =
    session.questionIds.length > 0 &&
    session.questionIds.some((questionId) => !getQuestionById(questionId, questions));

  const hydrateQuestions = useCallback(async () => {
    setQuestionLoadState("loading");
    setQuestionLoadError("");

    try {
      const loadedQuestions = await loadQuestionBank();
      setQuestions(loadedQuestions);
      setQuestionLoadState("idle");
      return loadedQuestions;
    } catch {
      setQuestionLoadState("error");
      setQuestionLoadError("The full question bank could not load. Check the connection and try again.");
      throw new Error("Question bank load failed.");
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (savedSession) {
      return;
    }

    window.localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(session));
  }, [savedSession, session]);

  useEffect(() => {
    if (savedSession || session.phase === "ready") {
      return;
    }

    mainPanelRef.current?.focus();
  }, [savedSession, session.currentIndex, session.phase]);

  useEffect(() => {
    if (session.phase === "ready" || !hasMissingSessionQuestions || questionLoadState === "loading") {
      return;
    }

    void hydrateQuestions().catch(() => undefined);
  }, [hasMissingSessionQuestions, hydrateQuestions, questionLoadState, session.phase]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timer = window.setTimeout(() => setToast(""), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const startQuiz = useCallback(async () => {
    try {
      const loadedQuestions = await hydrateQuestions();
      const pickedQuestions = pickQuizQuestions(QUESTIONS_PER_SESSION, loadedQuestions);
      setSession({
        phase: "playing",
        currentIndex: 0,
        answers: [],
        questionIds: pickedQuestions.map((question) => question.id)
      });
      setSavedSession(null);
      setToast("");
    } catch {
      setToast("Question bank failed to load.");
    }
  }, [hydrateQuestions]);

  const continueSavedSession = useCallback(async () => {
    if (!savedSession) {
      return;
    }

    try {
      if (savedSession.questionIds.some((questionId) => !getQuestionById(questionId, questions))) {
        await hydrateQuestions();
      }

      setSession(savedSession);
      setSavedSession(null);
      setToast("");
    } catch {
      setToast("Saved question bank failed to load.");
    }
  }, [hydrateQuestions, questions, savedSession]);

  const retryQuestionLoad = useCallback(() => {
    void hydrateQuestions().catch(() => setToast("Question bank failed to load."));
  }, [hydrateQuestions]);

  const restartQuiz = useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(QUIZ_STORAGE_KEY);
    }

    setSession({
      ...initialSession,
      questionIds: []
    });
    setSavedSession(null);
    setToast("");
  }, []);

  const selectAnswer = useCallback(
    (selectedIndex: number) => {
      if (session.phase !== "playing" || currentAnswer || !currentQuestion) {
        return;
      }

      const isCorrect = selectedIndex === currentQuestion.correctIndex;
      setSession((previous) => ({
        ...previous,
        answers: [
          ...previous.answers,
          {
            questionId: currentQuestion.id,
            selectedIndex,
            isCorrect
          }
        ]
      }));
    },
    [currentAnswer, currentQuestion, session.phase]
  );

  const advanceQuestion = useCallback(() => {
    if (session.phase !== "playing" || !currentAnswer) {
      return;
    }

    setSession((previous) => {
      if (previous.currentIndex >= totalQuestions - 1) {
        return {
          ...previous,
          phase: "finished"
        };
      }

      return {
        ...previous,
        currentIndex: previous.currentIndex + 1
      };
    });
  }, [currentAnswer, session.phase, totalQuestions]);

  const shareScore = useCallback(async () => {
    const shareText = getShareText(score, totalQuestions, result);
    const sharePayload = {
      title: "Real Fan Test",
      text: shareText,
      url: "https://realfantest.com"
    };
    const canUseNativeShare =
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      window.isSecureContext &&
      typeof navigator.share === "function" &&
      (!navigator.canShare || navigator.canShare(sharePayload));

    try {
      if (canUseNativeShare) {
        await navigator.share(sharePayload);
        setToast("Share sheet opened.");
        return;
      }

      await navigator.clipboard.writeText(shareText);
      setToast("Share text copied.");
    } catch {
      try {
        await navigator.clipboard.writeText(shareText);
        setToast("Share text copied.");
      } catch {
        setToast("Copy failed. The score text is visible on this screen.");
      }
    }
  }, [result, score, totalQuestions]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      if (savedSession && event.key === "Enter" && questionLoadState !== "loading") {
        event.preventDefault();
        void continueSavedSession();
        return;
      }

      if (session.phase === "ready" && event.key === "Enter" && questionLoadState !== "loading") {
        event.preventDefault();
        void startQuiz();
        return;
      }

      if (session.phase !== "playing" || !currentQuestion) {
        return;
      }

      const keyAsNumber = Number(event.key);
      if (!currentAnswer && keyAsNumber >= 1 && keyAsNumber <= currentQuestion.options.length) {
        event.preventDefault();
        selectAnswer(keyAsNumber - 1);
        return;
      }

      if (currentAnswer && (event.key === "Enter" || event.key === "ArrowRight")) {
        event.preventDefault();
        advanceQuestion();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    advanceQuestion,
    continueSavedSession,
    currentAnswer,
    currentQuestion,
    questionLoadState,
    savedSession,
    selectAnswer,
    session.phase,
    startQuiz
  ]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#quiz-panel">
        Skip to quiz
      </a>

      <header className="topbar" aria-label="Real Fan Test status">
        <div className="brand-lockup" aria-label="Real Fan Test">
          <LogoMark />
          <span>Real Fan Test</span>
        </div>
        <div className="score-strip" aria-label={`Current score ${score} of ${totalQuestions}`}>
          <Trophy aria-hidden="true" size={18} />
          <span>
            {score}/{totalQuestions}
          </span>
        </div>
      </header>

      <main className="game-grid">
        <section
          id="quiz-panel"
          className="quiz-panel"
          aria-label="Football fan test"
          aria-busy={questionLoadState === "loading"}
          ref={mainPanelRef}
          tabIndex={-1}
        >
          {savedSession ? (
            <ResumeScreen
              onContinue={() => void continueSavedSession()}
              onRestart={restartQuiz}
              savedSession={savedSession}
              totalQuestions={totalQuestions}
            />
          ) : session.phase === "ready" ? (
            <ReadyScreen
              isLoadingQuestions={questionLoadState === "loading"}
              loadError={questionLoadError}
              onStart={() => void startQuiz()}
            />
          ) : currentQuestion ? (
            session.phase === "finished" ? (
              <ResultScreen
                onRestart={restartQuiz}
                onShare={shareScore}
                result={result}
                score={score}
                totalQuestions={totalQuestions}
              />
            ) : (
              <QuestionScreen
                answer={currentAnswer}
                onAdvance={advanceQuestion}
                onSelect={selectAnswer}
                progress={progress}
                question={currentQuestion}
                questionNumber={session.currentIndex + 1}
                score={score}
                totalQuestions={totalQuestions}
              />
            )
          ) : (
            <QuestionBankStatus
              isLoading={questionLoadState === "loading"}
              loadError={questionLoadError}
              onRestart={restartQuiz}
              onRetry={retryQuestionLoad}
            />
          )}
        </section>

        <aside className="match-rail" aria-label="Test details">
          <div className="rail-block">
            <span className="rail-label">Categories</span>
            <strong>Players, World Cup, Rules</strong>
          </div>
          <div className="rail-block">
            <span className="rail-label">Pace</span>
            <strong>
              {QUESTIONS_PER_SESSION} questions from {questions.length}
              {questions === staticQuestionBank ? "+" : ""}
            </strong>
          </div>
          <div className="rail-block">
            <span className="rail-label">Result</span>
            <strong>Real or fake fan</strong>
          </div>
        </aside>
      </main>

      <SeoFaq />

      <div className="toast-region" aria-live="polite" aria-atomic="true">
        {toast}
      </div>
    </div>
  );
}

function ReadyScreen({
  isLoadingQuestions,
  loadError,
  onStart
}: {
  isLoadingQuestions: boolean;
  loadError: string;
  onStart: () => void;
}) {
  return (
    <div className="ready-screen">
      <div className="status-row">
        <span className="status-chip">World Cup</span>
        <span className="status-chip">Rules</span>
        <span className="status-chip">Players</span>
      </div>
      <div className="headline-stack">
        <p className="small-caps">realfantest.com</p>
        <h1>Are you a real football fan?</h1>
        <p className="lede">Start a fresh 10-question run across World Cup players, history, and rules.</p>
        <ThresholdList />
      </div>
      {loadError ? <p className="load-message">{loadError}</p> : null}
      <button
        aria-keyshortcuts="Enter"
        className="primary-action"
        disabled={isLoadingQuestions}
        type="button"
        onClick={onStart}
      >
        <Play aria-hidden="true" size={20} />
        {isLoadingQuestions ? "Loading Questions" : "Start Test"}
      </button>
    </div>
  );
}

function ResumeScreen({
  onContinue,
  onRestart,
  savedSession,
  totalQuestions
}: {
  onContinue: () => void;
  onRestart: () => void;
  savedSession: QuizSession;
  totalQuestions: number;
}) {
  const answeredCount = savedSession.answers.length;
  const savedScore = savedSession.answers.filter((answer) => answer.isCorrect).length;

  return (
    <div className="ready-screen">
      <div className="resume-panel">
        <p className="small-caps">Saved test found</p>
        <h1>Continue your fan test?</h1>
        <p className="lede">Continue saved progress or restart from question one.</p>
        <p className="lede">
          You are on question {Math.min(savedSession.currentIndex + 1, totalQuestions)} of {totalQuestions}, with {savedScore} right from {answeredCount} answered.
        </p>
        <div className="action-row">
          <button aria-keyshortcuts="Enter" className="primary-action" type="button" onClick={onContinue}>
            <Play aria-hidden="true" size={20} />
            Continue Test
          </button>
          <button className="secondary-action" type="button" onClick={onRestart}>
            <RefreshCw aria-hidden="true" size={20} />
            Restart
          </button>
        </div>
      </div>
      <ThresholdList />
    </div>
  );
}

function ThresholdList() {
  return (
    <div className="threshold-list" aria-label="Fan rank thresholds">
      {resultBands.map((band) => (
        <span key={band.title}>{band.thresholdLabel}</span>
      ))}
    </div>
  );
}

function QuestionBankStatus({
  isLoading,
  loadError,
  onRestart,
  onRetry
}: {
  isLoading: boolean;
  loadError: string;
  onRestart: () => void;
  onRetry: () => void;
}) {
  return (
    <div className="ready-screen">
      <div className="headline-stack">
        <p className="small-caps">{isLoading ? "Loading question bank" : "Question bank unavailable"}</p>
        <h1>{isLoading ? "Loading full-time squad data" : "Reload the question bank"}</h1>
        <p className="lede">
          {loadError || "The saved session uses FIFA squad questions that need the full question bank."}
        </p>
      </div>
      <div className="action-row">
        <button className="primary-action" disabled={isLoading} type="button" onClick={onRetry}>
          <RefreshCw aria-hidden="true" size={20} />
          {isLoading ? "Loading" : "Try Again"}
        </button>
        <button className="secondary-action" type="button" onClick={onRestart}>
          <RefreshCw aria-hidden="true" size={20} />
          Restart
        </button>
      </div>
    </div>
  );
}

function QuestionScreen({
  answer,
  onAdvance,
  onSelect,
  progress,
  question,
  questionNumber,
  score,
  totalQuestions
}: {
  answer?: AnswerRecord;
  onAdvance: () => void;
  onSelect: (selectedIndex: number) => void;
  progress: number;
  question: QuizQuestion;
  questionNumber: number;
  score: number;
  totalQuestions: number;
}) {
  const correctOption = question.options[question.correctIndex];
  const feedbackId = `${question.id}-feedback`;

  return (
    <div className="question-screen">
      <div className="progress-header">
        <div>
          <span className="small-caps">Question {questionNumber} of {totalQuestions}</span>
          <div className="progress-track" aria-label={`Progress ${progress}%`}>
            <span style={{ inlineSize: `${progress}%` }} />
          </div>
        </div>
        <div className="live-score">
          <Shield aria-hidden="true" size={18} />
          <span>{score} right</span>
        </div>
      </div>

      <div className="question-meta" aria-label={`${question.category}, ${question.difficulty} difficulty`}>
        <span>{question.category}</span>
        <span>{question.difficulty}</span>
      </div>

      <div className="visual-question">
        <QuestionVisual question={question} />
        <h2>{question.prompt}</h2>
      </div>

      <div
        className={`answer-list ${question.optionImages ? "has-photo-options" : ""}`}
        role="list"
        aria-label="Answer options"
        aria-describedby={answer ? feedbackId : undefined}
      >
        {question.options.map((option, index) => (
          <AnswerButton
            correctIndex={question.correctIndex}
            index={index}
            isAnswered={Boolean(answer)}
            key={`${option}-${index}`}
            onSelect={onSelect}
            option={option}
            optionImage={question.optionImages?.[index]}
            selectedIndex={answer?.selectedIndex}
          />
        ))}
      </div>

      <div className={`feedback-box ${answer ? "is-visible" : ""}`} id={feedbackId} role="status" aria-live="polite">
        {answer ? (
          <>
            <div className="feedback-title">
              {answer.isCorrect ? <Check aria-hidden="true" size={20} /> : <X aria-hidden="true" size={20} />}
              <strong>{answer.isCorrect ? "Correct" : `Correct answer: ${correctOption}`}</strong>
            </div>
            <p>{question.explanation}</p>
            <button aria-keyshortcuts="Enter ArrowRight" className="next-action" type="button" onClick={onAdvance}>
              {questionNumber === totalQuestions ? "See Result" : "Next Question"}
              <ChevronRight aria-hidden="true" size={19} />
            </button>
          </>
        ) : (
          <p>Select an answer to lock it in.</p>
        )}
      </div>
    </div>
  );
}

function AnswerButton({
  correctIndex,
  index,
  isAnswered,
  onSelect,
  option,
  optionImage,
  selectedIndex
}: {
  correctIndex: number;
  index: number;
  isAnswered: boolean;
  onSelect: (selectedIndex: number) => void;
  option: string;
  optionImage?: QuizOptionImage;
  selectedIndex?: number;
}) {
  const isSelected = selectedIndex === index;
  const isCorrect = correctIndex === index;
  const stateClass = isAnswered && isCorrect ? "is-correct" : isAnswered && isSelected ? "is-wrong" : "";
  const optionLabel = String.fromCharCode(65 + index);
  const ariaLabel = optionImage ? `Option ${optionLabel} player photo` : undefined;

  return (
    <button
      aria-label={ariaLabel}
      aria-keyshortcuts={`${index + 1}`}
      aria-pressed={isSelected}
      className={`answer-button ${optionImage ? "has-option-image" : ""} ${stateClass}`}
      disabled={isAnswered}
      onClick={() => onSelect(index)}
      type="button"
    >
      <span className="option-index">{optionLabel}</span>
      {optionImage ? <img className="answer-photo" src={optionImage.imageUrl} alt="" aria-hidden="true" /> : <span>{option}</span>}
      {isAnswered && isCorrect ? <Check aria-hidden="true" size={19} /> : null}
      {isAnswered && isSelected && !isCorrect ? <X aria-hidden="true" size={19} /> : null}
    </button>
  );
}

function ResultScreen({
  onRestart,
  onShare,
  result,
  score,
  totalQuestions
}: {
  onRestart: () => void;
  onShare: () => void;
  result: ResultBand;
  score: number;
  totalQuestions: number;
}) {
  const shareText = getShareText(score, totalQuestions, result);

  return (
    <div className="result-screen">
      <div className="result-medal" aria-hidden="true">
        <Trophy size={34} />
      </div>
      <p className="small-caps">Final whistle</p>
      <h1>{result.title}</h1>
      <p className="result-label">{result.label}</p>
      <p className="badge-line">{result.badgeLine}</p>
      <div className="score-card" aria-label={`Final score ${score} of ${totalQuestions}`}>
        <span>{score}</span>
        <span>/ {totalQuestions}</span>
      </div>
      <p className="result-summary">{result.summary}</p>
      <p className="challenge-copy">Can you beat {score}/{totalQuestions}?</p>
      <ThresholdList />
      <div className="share-copy" aria-label="Share text">
        <Clipboard aria-hidden="true" size={18} />
        <span>{shareText}</span>
      </div>
      <div className="action-row">
        <button className="primary-action" type="button" onClick={onShare}>
          <Share2 aria-hidden="true" size={20} />
          Share Score
        </button>
        <button className="secondary-action" type="button" onClick={onRestart}>
          <RefreshCw aria-hidden="true" size={20} />
          Restart
        </button>
      </div>
    </div>
  );
}

function QuestionVisual({ question }: { question: QuizQuestion }) {
  const shouldShowVisualCard = question.visual.showCard !== false;

  return (
    <div className={`question-visual accent-${question.visual.accent} ${question.imageUrl ? "has-photo" : ""}`}>
      <div className="pitch-lines">
        <CircleDot size={28} />
      </div>
      {question.imageUrl ? (
        <img className="player-photo" src={question.imageUrl} alt={question.imageAlt ?? question.visual.label} />
      ) : null}
      {shouldShowVisualCard ? (
        <div className="visual-card" aria-hidden={question.imageUrl ? undefined : "true"}>
          <span>{question.visual.label}</span>
          <small>{question.visual.sublabel}</small>
        </div>
      ) : null}
    </div>
  );
}

function SeoFaq() {
  return (
    <details className="seo-section" aria-labelledby="seo-heading">
      <summary>About this test</summary>
      <p className="small-caps">Football quiz FAQ</p>
      <h2 id="seo-heading">Real Fan Test</h2>
      <p className="seo-intro">
        Real Fan Test is a fast football quiz for fans who want to check their World Cup knowledge, player
        recognition, football history, and match-rule instincts in a shareable score format.
      </p>
      <div className="faq-list">
        <div className="faq-row">
          <h3>What topics are in the football fan test?</h3>
          <p>
            The question bank combines 2026 World Cup squad prompts, player photo recognition, tournament
            records, iconic finals, refereeing decisions, and core Laws of the Game concepts.
          </p>
        </div>
        <div className="faq-row">
          <h3>How is the fan score calculated?</h3>
          <p>
            Each session draws {QUESTIONS_PER_SESSION} questions from a larger bank. Correct answers raise your
            score, and the final band separates Real Fan, Matchday Regular, Casual Supporter, and Fake Fan Alert
            results.
          </p>
        </div>
        <div className="faq-row">
          <h3>Can I share my result?</h3>
          <p>
            The result screen creates a short share line with your score and fan rank, so the quiz can spread
            cleanly through chats, social feeds, and matchday groups.
          </p>
        </div>
        <div className="faq-row">
          <h3>Is Real Fan Test affiliated with FIFA?</h3>
          <p>
            No. Real Fan Test is an independent fan quiz. Official team pages and public football law references
            are used to keep the questions grounded.
          </p>
        </div>
      </div>
      <p className="source-note">Independent fan quiz. Not affiliated with FIFA, clubs, or tournament organizers.</p>
    </details>
  );
}

function LogoMark() {
  return (
    <svg className="logo-mark" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <rect x="3" y="6" width="34" height="28" rx="4" />
      <path d="M7 20h26M20 6v28" />
      <circle cx="20" cy="20" r="7" />
      <path d="M13 12h14" />
    </svg>
  );
}
